import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, ToggleButtonGroup, ToggleButton, Select, MenuItem, InputLabel,
  FormControl, FormHelperText, IconButton, Box, CircularProgress, LinearProgress, Chip, Typography,
  Autocomplete,
} from '@mui/material';
import { Close, AddPhotoAlternate, ScubaDiving, Notes } from '@mui/icons-material';
import { postService, PostVisibility, photoUrl } from '../services/postService';
import { diveService as accountDiveService } from '../services/diveService';
import { Dive } from '../types/Dive';
import { authService } from '../services/authService';
import { useBuddies } from '../hooks/useBuddies';
import { useGroups } from '../hooks/useGroups';
import { useClubs } from '../hooks/useClubs';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { ACCEPTED_MEDIA_TYPES, MAX_MEDIA_PER_POST, MAX_VIDEOS_PER_POST, isVideoFile, isVideoKey, validateMediaFile } from '../utils/media';
import MentionTextField from './MentionTextField';
import { requestTagInvites } from '../utils/invite';

// The composer's own "who can see this" choices — a superset of PostVisibility. The last
// two aren't real Visibility values: picking one prompts for a specific group/club, and
// submission is routed through postService.createGroupPost/createClubPost so it reuses
// those endpoints' own membership checks and fan-out instead of a new visibility value.
const AudienceMode = {
  ...PostVisibility,
  SpecificGroup: 'SpecificGroup',
  SpecificClub: 'SpecificClub',
} as const;

interface PostComposerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (post: any) => void;
  /** Pre-select "attach a dive" mode with this dive already chosen (opened from a dive page). */
  initialDiveId?: string;
  /** Which mode to open in when no initialDiveId is given — "dive" lets the user pick one of their dives. */
  initialMode?: 'text' | 'dive';
  /** Files already picked by the caller (e.g. the feed's Photo shortcut) — uploaded as soon as the composer opens. */
  initialFiles?: File[];
  /** Post about a dive site's hub: starts Public and tags the post to the site. */
  diveSite?: { slug: string; name: string };
}

const MAX_PENDING_TAGS = 5;

const PostComposer: React.FC<PostComposerProps> = ({ open, onClose, onCreated, initialDiveId, initialMode, initialFiles, diveSite }) => {
  const [mode, setMode] = useState<'text' | 'dive'>(initialDiveId ? 'dive' : (initialMode ?? 'text'));
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<string>(diveSite ? AudienceMode.Public : AudienceMode.Buddies);
  const [targetGroupId, setTargetGroupId] = useState<string>('');
  const [targetClubId, setTargetClubId] = useState<string>('');
  const [photoKeys, setPhotoKeys] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  // Which file of this batch is going up, and how far — drives the progress bar.
  const [uploadProgress, setUploadProgress] = useState<{ index: number; total: number; fraction: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});

  const [dives, setDives] = useState<Dive[]>([]);
  const [divesLoading, setDivesLoading] = useState(false);
  const [selectedDiveId, setSelectedDiveId] = useState<string>(initialDiveId ?? '');

  const currentUserId = authService.getUser()?.userId ?? null;
  const { buddies } = useBuddies(currentUserId);
  const { groups } = useGroups(currentUserId);
  const { clubs } = useClubs(currentUserId);
  const [buddyProfiles, setBuddyProfiles] = useState<Record<string, UserPublicProfile>>({});
  const [taggedUserIds, setTaggedUserIds] = useState<string[]>([]);
  const [taggedGroupIds, setTaggedGroupIds] = useState<string[]>([]);
  const [taggedClubIds, setTaggedClubIds] = useState<string[]>([]);
  const [pendingTagNames, setPendingTagNames] = useState<string[]>([]);
  const [pendingTagInput, setPendingTagInput] = useState('');

  useEffect(() => {
    const missing = buddies.map(b => b.buddyUserId).filter(id => !buddyProfiles[id]);
    if (missing.length === 0) return;
    Promise.allSettled(missing.map(id => buddyService.getUserProfile(id))).then(results => {
      const next: Record<string, UserPublicProfile> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[missing[i]] = r.value;
      });
      if (Object.keys(next).length > 0) setBuddyProfiles(prev => ({ ...prev, ...next }));
    });
  }, [buddies, buddyProfiles]);

  const buddyOptions = buddies.map(b => ({
    userId: b.buddyUserId,
    displayName: buddyProfiles[b.buddyUserId]?.displayName ?? b.buddyUserId,
  }));

  useEffect(() => {
    if (!open || mode !== 'dive' || dives.length > 0) return;
    setDivesLoading(true);
    accountDiveService.getMyDives()
      .then(setDives)
      .catch(() => setError('Failed to load your dives'))
      .finally(() => setDivesLoading(false));
  }, [open, mode, dives.length]);

  const resetAndClose = () => {
    setText('');
    setPhotoKeys([]);
    Object.values(localPreviews).forEach(URL.revokeObjectURL);
    setLocalPreviews({});
    setSelectedDiveId(initialDiveId ?? '');
    setMode(initialDiveId ? 'dive' : (initialMode ?? 'text'));
    setVisibility(diveSite ? AudienceMode.Public : AudienceMode.Buddies);
    setTargetGroupId('');
    setTargetClubId('');
    setTaggedUserIds([]);
    setTaggedGroupIds([]);
    setTaggedClubIds([]);
    setPendingTagNames([]);
    setPendingTagInput('');
    setError(null);
    onClose();
  };

  // Posting to a specific group or club always goes through that group's/club's own post
  // endpoint (same one its own page uses), so it gets that endpoint's membership check and
  // member fan-out for free — neither supports the "Log a Dive" mode, so switching to one
  // of these audiences drops back to text/photo.
  const handleVisibilityChange = (value: string) => {
    setVisibility(value);
    if (value === AudienceMode.SpecificGroup || value === AudienceMode.SpecificClub) {
      setMode('text');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    void addFiles(files);
  };

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    let keys = photoKeys;
    try {
      for (const file of files) {
        if (keys.length >= MAX_MEDIA_PER_POST) { setError(`A post can have at most ${MAX_MEDIA_PER_POST} photos/videos`); break; }
        if (isVideoFile(file) && keys.filter(isVideoKey).length >= MAX_VIDEOS_PER_POST) { setError(`A post can have at most ${MAX_VIDEOS_PER_POST} videos`); continue; }
        const problem = await validateMediaFile(file);
        if (problem) { setError(`${file.name}: ${problem}`); continue; }
        setUploadProgress({ index: files.indexOf(file) + 1, total: files.length, fraction: 0 });
        const key = await postService.uploadPhoto(file, fraction =>
          setUploadProgress(p => (p ? { ...p, fraction } : p)));
        keys = [...keys, key];
        setPhotoKeys(keys);
        // Local preview for the thumbnail strip — the uploaded object is public, but showing the local file avoids a re-download.
        setLocalPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
      }
    } catch {
      setError('Upload failed — please try again');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0) void addFiles(initialFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  const removeMedia = (key: string) => {
    setPhotoKeys(prev => prev.filter(k => k !== key));
    setLocalPreviews(prev => {
      if (prev[key]) URL.revokeObjectURL(prev[key]);
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // The server only accepts not-yet-member tags on audiences a brand-new member can be let into one post of.
  const canTagNonMembers = visibility === AudienceMode.Public || visibility === AudienceMode.Buddies || visibility === AudienceMode.Followers;

  const addPendingTag = () => {
    const name = pendingTagInput.trim();
    if (!name) return;
    if (pendingTagNames.length >= MAX_PENDING_TAGS) { setError(`You can tag at most ${MAX_PENDING_TAGS} people who aren't on the app yet`); return; }
    if (!pendingTagNames.some(n => n.toLowerCase() === name.toLowerCase())) setPendingTagNames(prev => [...prev, name]);
    setPendingTagInput('');
    setError(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let post;
      // A name typed but never added with Enter/Add still counts — losing it silently would surprise people.
      const typed = pendingTagInput.trim();
      const namesToSend = canTagNonMembers
        ? [...pendingTagNames, ...(typed && !pendingTagNames.some(n => n.toLowerCase() === typed.toLowerCase()) ? [typed] : [])]
        : [];
      if (visibility === AudienceMode.SpecificGroup) {
        post = await postService.createGroupPost(targetGroupId, { text: text || undefined, photoKeys });
      } else if (visibility === AudienceMode.SpecificClub) {
        post = await postService.createClubPost(targetClubId, { text: text || undefined, photoKeys, taggedUserIds, taggedGroupIds, taggedClubIds });
      } else if (mode === 'dive') {
        post = await postService.createDiveLogPost(selectedDiveId, { text: text || undefined, photoKeys, visibility, taggedUserIds, taggedGroupIds, taggedClubIds, pendingTagNames: namesToSend });
      } else {
        post = await postService.createTextPost({ text: text || undefined, photoKeys, visibility, diveSiteSlug: diveSite && visibility === AudienceMode.Public ? diveSite.slug : undefined, taggedUserIds, taggedGroupIds, taggedClubIds, pendingTagNames: namesToSend });
      }
      onCreated(post);
      resetAndClose();
      if (post?.pendingTags?.length) requestTagInvites(post.postId);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = (() => {
    if (submitting) return false;
    if (visibility === AudienceMode.SpecificGroup) return !!targetGroupId && (!!text.trim() || photoKeys.length > 0);
    if (visibility === AudienceMode.SpecificClub) return !!targetClubId && (!!text.trim() || photoKeys.length > 0);
    return mode === 'dive' ? !!selectedDiveId : (!!text.trim() || photoKeys.length > 0);
  })();

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {diveSite ? `Post about ${diveSite.name}` : 'New Post'}
        <IconButton onClick={resetAndClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => v && setMode(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="text"><Notes fontSize="small" sx={{ mr: 1 }} /> Text / Photo</ToggleButton>
            <ToggleButton
              value="dive"
              disabled={visibility === AudienceMode.SpecificGroup || visibility === AudienceMode.SpecificClub}
            >
              <ScubaDiving fontSize="small" sx={{ mr: 1 }} /> Log a Dive
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === 'dive' && (
            <FormControl fullWidth size="small">
              <InputLabel>Dive</InputLabel>
              <Select
                value={selectedDiveId}
                label="Dive"
                onChange={(e) => setSelectedDiveId(e.target.value)}
                disabled={divesLoading}
              >
                {dives.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    {(d.location || 'Unknown location')} · {new Date(d.startTime).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <MentionTextField
            multiline
            minRows={3}
            placeholder={
              visibility === AudienceMode.SpecificGroup
                ? "What's happening?"
                : mode === 'dive' ? 'Add a caption (optional)… Type @ to tag a buddy or group' : "What's happening? Type @ to tag a buddy or group"
            }
            value={text}
            onChange={setText}
            taggedUserIds={taggedUserIds}
            onTaggedUserIdsChange={setTaggedUserIds}
            taggedGroupIds={taggedGroupIds}
            onTaggedGroupIdsChange={setTaggedGroupIds}
            taggedClubIds={taggedClubIds}
            onTaggedClubIdsChange={setTaggedClubIds}
            fullWidth
          />

          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MEDIA_TYPES}
              multiple
              hidden
              onChange={handleFileSelect}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={uploading ? <CircularProgress size={14} /> : <AddPhotoAlternate />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              Add Photos / Video
            </Button>
            {uploadProgress && (
              <Box sx={{ mt: 1.5 }}>
                <LinearProgress variant="determinate" value={Math.round(uploadProgress.fraction * 100)} />
                <Typography variant="caption" color="text.secondary">
                  Uploading {uploadProgress.total > 1 ? `${uploadProgress.index} of ${uploadProgress.total} · ` : ''}{Math.round(uploadProgress.fraction * 100)}%
                </Typography>
              </Box>
            )}
            {photoKeys.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                {photoKeys.length} of {MAX_MEDIA_PER_POST} attached · videos up to 60 s
              </Typography>
            )}
            {photoKeys.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto', pb: 0.5 }}>
                {photoKeys.map(key => (
                  <Box key={key} sx={{ position: 'relative', flex: '0 0 auto' }}>
                    {isVideoKey(key) ? (
                      <video src={localPreviews[key] ?? photoUrl(key)} muted playsInline style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                    ) : (
                      <img src={localPreviews[key] ?? photoUrl(key)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                    )}
                    <IconButton
                      size="small"
                      aria-label="Remove attachment"
                      onClick={() => removeMedia(key)}
                      sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 0.25, '&:hover': { bgcolor: 'background.paper' } }}
                    >
                      <Close sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Who can see this</InputLabel>
            <Select value={visibility} label="Who can see this" onChange={(e) => handleVisibilityChange(e.target.value)}>
              <MenuItem value={PostVisibility.Public}>Public</MenuItem>
              <MenuItem value={PostVisibility.Buddies}>Dive Buddies only</MenuItem>
              <MenuItem value={PostVisibility.Followers}>Followers only</MenuItem>
              <MenuItem value={PostVisibility.Private}>Only me</MenuItem>
              {groups.length > 0 && <MenuItem value={AudienceMode.SpecificGroup}>A specific dive group…</MenuItem>}
              {clubs.length > 0 && <MenuItem value={AudienceMode.SpecificClub}>A specific club…</MenuItem>}
            </Select>
            {visibility === AudienceMode.Public && (
              <FormHelperText>
                {diveSite
                  ? `Posting to the ${diveSite.name} hub: anyone can see this, including people who aren't signed in.`
                  : "Anyone can see this, including people who aren't signed in."}
              </FormHelperText>
            )}
            {diveSite && visibility !== AudienceMode.Public && (
              <FormHelperText>
                This post won't appear on the {diveSite.name} hub, because hub posts have to be Public.
              </FormHelperText>
            )}
          </FormControl>

          {visibility === AudienceMode.SpecificGroup && (
            <FormControl fullWidth size="small">
              <InputLabel>Dive Group</InputLabel>
              <Select value={targetGroupId} label="Dive Group" onChange={(e) => setTargetGroupId(e.target.value)}>
                {groups.map(g => (
                  <MenuItem key={g.groupId} value={g.groupId}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {visibility === AudienceMode.SpecificClub && (
            <FormControl fullWidth size="small">
              <InputLabel>Club</InputLabel>
              <Select value={targetClubId} label="Club" onChange={(e) => setTargetClubId(e.target.value)}>
                {clubs.map(c => (
                  <MenuItem key={c.clubId} value={c.clubId}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {visibility !== AudienceMode.SpecificGroup && buddyOptions.length > 0 && (
            <Autocomplete
              multiple
              size="small"
              options={buddyOptions}
              getOptionLabel={(o) => o.displayName}
              isOptionEqualToValue={(o, v) => o.userId === v.userId}
              value={buddyOptions.filter(o => taggedUserIds.includes(o.userId))}
              onChange={(_, value) => setTaggedUserIds(value.map(v => v.userId))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.displayName} size="small" {...getTagProps({ index })} key={option.userId} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tag dive buddies (optional)" placeholder="Search buddies…" />
              )}
            />
          )}

          {canTagNonMembers && (
            <Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="Tag someone who isn't on the app yet (optional)"
                  placeholder="Their name — you'll get a link to send them"
                  value={pendingTagInput}
                  inputProps={{ maxLength: 60 }}
                  onChange={(e) => setPendingTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPendingTag(); } }}
                />
                <Button onClick={addPendingTag} disabled={!pendingTagInput.trim()}>Add</Button>
              </Stack>
              {pendingTagNames.length > 0 && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {pendingTagNames.map(name => (
                    <Chip key={name} size="small" label={name} onDelete={() => setPendingTagNames(prev => prev.filter(n => n !== name))} />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {error && <Chip label={error} color="error" variant="outlined" />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? <CircularProgress size={20} /> : 'Post'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostComposer;
